import { useCallback, useMemo, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { FieldError, FieldErrors, FieldValues } from 'react-hook-form'

import { Props as RootProps } from '../components/Root'

interface Props {
  mapper?: (field: string, message: string) => [string, FieldError] | undefined
}

interface Result<T extends FieldValues = FieldValues> {
  parseApolloErrors: (graphQLErrors: ApolloError['graphQLErrors']) => void
  setServerError: (type: string, message: string) => void
  errors: RootProps<T>['serverErrors']
}

export function useErrorHelpers<T extends FieldValues = FieldValues>({
  mapper = (field, message) => [
    field,
    {
      type: 'default',
      message
    }
  ]
}: Props): Result<T> {
  const [globalError, setGlobalError] = useState<FieldError>()
  const [serverErrors, setServerErrors] = useState<FieldErrors<T>>()

  const parseApolloErrors = useCallback(
    (graphQLErrors: ApolloError['graphQLErrors']) => {
      const errors = graphQLErrors
        .map(function (error) {
          if (!error.extensions) return null
          if (!('field' in error.extensions)) return null

          const field = error.extensions.field

          if (typeof field !== 'string') return null
          return mapper(field, error.message)
        })
        .filter(field => field != null)

      setServerErrors(Object.fromEntries(errors) as FieldErrors<T>)
    },
    [mapper, setServerErrors]
  )

  const setServerError = useCallback(
    (type: string, message: string) => {
      setGlobalError({
        message,
        type
      })
    },
    [setGlobalError]
  )

  const errors = useMemo(() => {
    let errors: FieldErrors<T> = {}

    if (serverErrors) {
      errors = { ...serverErrors }
    }

    if (globalError) {
      errors.root = {
        serverError: globalError
      }
    }

    return errors
  }, [serverErrors, globalError])

  return {
    parseApolloErrors,
    setServerError,
    errors
  }
}
